<div class="crawl-management">
  <div class="header">
    <h1>Crawl Management</h1>
    <p>Manage domain crawls and monitor crawl progress</p>
  </div>

  <div class="add-domain-panel">
    <h2 class="add-domain-title">Add domain</h2>
    <p class="add-domain-hint">Check sitemap size (no crawl cost), then start a crawl up to 750 pages — same as elsewhere in the app.</p>
    <div class="add-domain-row">
      <input
        type="text"
        class="add-domain-input"
        [(ngModel)]="newDomainInput"
        placeholder="example.com or https://www.example.com"
        (keyup.enter)="estimateNewDomain()"
      />
      <button type="button" class="btn btn-secondary btn-sm" (click)="estimateNewDomain()"
        [disabled]="newDomainEstimateLoading || newDomainStartLoading">
        {{ newDomainEstimateLoading ? 'Checking…' : 'Check sitemap' }}
      </button>
      <button type="button" class="btn btn-primary btn-sm" (click)="startCrawlForNewDomain()"
        [disabled]="newDomainEstimateLoading || newDomainStartLoading || (newDomainEstimate && !hasSitemapSelection())">
        {{ newDomainStartLoading ? 'Starting…' : 'Start crawl' }}
      </button>
    </div>
    <div *ngIf="newDomainEstimateError" class="inline-error">❌ {{ newDomainEstimateError }}</div>
    <div *ngIf="newDomainStartError" class="inline-error">❌ {{ newDomainStartError }}</div>
    <div *ngIf="newDomainEstimate as est" class="estimate-summary">
      <p *ngIf="est.aliasApplied && est.canonicalDomain" class="estimate-line alias-note">
        Canonical domain: <strong>{{ est.canonicalDomain }}</strong>
        <span *ngIf="est.requestedDomain"> (from {{ est.requestedDomain }})</span>
      </p>
      <p class="estimate-line"><span class="estimate-label">Discovered URLs:</span> {{ est.totalPagesInSitemap | number }}<span *ngIf="est.sitemapParseTruncated" class="estimate-warn"> (parse truncated)</span></p>
      <p class="estimate-line"><span class="estimate-label">Selected URLs:</span> {{ getSelectedDiscoveredUrlCount() | number }}</p>
      <p class="estimate-line"><span class="estimate-label">Included at cap ({{ est.limit }}):</span> {{ getSelectedIncludedAtCap() | number }}</p>
      <p class="estimate-line"><span class="estimate-label">Est. crawl cost:</span> ${{ getSelectedEstimatedCost() | number:'1.2-2' }}</p>
      <p *ngIf="est.sitemapUrl" class="estimate-line estimate-sitemap-url"><span class="estimate-label">Primary sitemap:</span> {{ est.sitemapUrl }}</p>
      <div *ngIf="(est.duplicateUrlsInSitemaps || 0) > 0" class="estimate-guardrail estimate-guardrail-info">
        ℹ️ Duplicate URLs exist across sitemaps ({{ est.duplicateUrlsInSitemaps | number }}). Row totals can be higher than "Selected URLs"; crawl processes each unique URL once.
      </div>
      <div *ngIf="(est.sitemapParseFailures || []).length > 0" class="estimate-guardrail">
        ⚠️ Parsed with warnings: {{ est.sitemapParseFailures?.length }} sitemap(s) failed to parse.
      </div>

      <div *ngIf="getLeafNodes(est.sitemapTree).length > 0" class="sitemap-controls">
        <div class="sitemap-controls-row">
          <label class="checkline">
            <input type="checkbox" [checked]="areAllSitemapsSelected()" (change)="setAllSitemapsSelected($any($event.target).checked)" />
            Select all leaf sitemaps
          </label>
        </div>
        <div class="sitemap-filter-row">
          <label class="strategy-label">
            Lastmod on/after:
            <input
              type="date"
              class="strategy-select"
              [ngModel]="minLastmodFrom"
              (ngModelChange)="onMinLastmodFromChange($event)"
            />
          </label>
          <label class="strategy-label">
            Per-sitemap cap mode:
            <select
              class="strategy-select"
              [ngModel]="perSitemapLimitMode"
              (ngModelChange)="onPerSitemapLimitModeChange($event)"
            >
              <option value="recent">Most recent first</option>
              <option value="oldest">Oldest first</option>
              <option value="balanced">Balanced (newest + oldest)</option>
            </select>
          </label>
        </div>
        <div class="sitemap-filter-note">
          Per-sitemap cap applies after lastmod filtering using the selected mode.
        </div>

        <div class="sitemap-list">
          <div
            class="sitemap-item"
            *ngFor="let leaf of getLeafNodes(est.sitemapTree)"
            [class.sitemap-item-disabled]="!selectedSitemaps.has(leaf.sitemapUrl)"
          >
            <div class="sitemap-item-header">
              <label class="checkline">
                <input
                  type="checkbox"
                  [checked]="selectedSitemaps.has(leaf.sitemapUrl)"
                  (change)="toggleSitemapSelection(leaf.sitemapUrl, $any($event.target).checked)"
                />
                <span class="sitemap-url">{{ leaf.sitemapUrl }}</span>
                <span class="sitemap-meta">({{ getLeafDisplayUrlCount(leaf) | number }} URLs)</span>
              </label>
              <label class="leaf-limit-label">
                Crawl
                <input
                  type="number"
                  min="1"
                  step="1"
                  class="leaf-limit-input"
                  [ngModel]="getPerSitemapLimit(leaf.sitemapUrl)"
                  (ngModelChange)="onPerSitemapLimitChange(leaf.sitemapUrl, $event)"
                  placeholder="all"
                />
                URLs
              </label>
            </div>
            <div class="sitemap-examples" *ngIf="(leaf.exampleUrls || []).length > 0">
              <div class="sitemap-examples-title">Examples:</div>
              <a
                *ngFor="let exampleUrl of (leaf.exampleUrls || []).slice(0, 2)"
                class="sitemap-example-link"
                [href]="exampleUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ exampleUrl }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="page-actions">
    <button (click)="loadDomains()" [disabled]="loadingDomains" class="btn btn-secondary btn-sm">
      {{ loadingDomains ? 'Loading…' : 'Refresh Domains' }}
    </button>
  </div>

  <div *ngIf="error" class="inline-error">❌ {{ error }}</div>

  <div *ngIf="loadingDomains && domains.length === 0" class="loading-msg">Loading domains…</div>

  <div *ngIf="!loadingDomains && domains.length === 0" class="empty-state">
    No domains yet. Use <strong>Add domain</strong> above or start from the Page Comparison page.
  </div>

  <div *ngIf="domains.length > 0" class="domains-list">
    <div *ngFor="let domain of domains" class="domain-card">

      <!-- Card header -->
      <div class="card-header">
        <span class="domain-name">{{ domain }}</span>
        <span [class]="'status-badge ' + getStatusClass(domainStatuses.get(domain)?.status || null)">
          {{ getStatusText(domainStatuses.get(domain)?.status || null) }}
        </span>
        <div class="card-actions">
          <button class="btn btn-secondary btn-xs" (click)="startCrawl(domain)"
            [disabled]="domainStatuses.get(domain)?.loading || deletingDomains.get(domain)">
            {{ domainStatuses.get(domain)?.loading ? 'Working…' : 'Start Crawl' }}
          </button>
          <button class="btn btn-outline btn-xs" (click)="loadCrawlStatus(domain)"
            [disabled]="domainStatuses.get(domain)?.loading || deletingDomains.get(domain)">
            Refresh
          </button>
          <button class="btn btn-danger btn-xs" (click)="deleteDomainData(domain)"
            [disabled]="domainStatuses.get(domain)?.loading || deletingDomains.get(domain)">
            {{ deletingDomains.get(domain) ? 'Deleting…' : 'Delete' }}
          </button>
        </div>
      </div>

      <div *ngIf="domainStatuses.get(domain)?.error" class="inline-error card-error">
        ❌ {{ domainStatuses.get(domain)?.error }}
      </div>

      <div *ngIf="domainStatuses.get(domain)?.status" class="card-body">

        <!-- Alias note -->
        <div class="alias-note" *ngIf="domainStatuses.get(domain)?.status?.aliasApplied && domainStatuses.get(domain)?.status?.canonicalDomain">
          Canonical: <strong>{{ domainStatuses.get(domain)?.status?.canonicalDomain }}</strong> (requested: {{ domain }})
        </div>

        <!-- Progress bar -->
        <div class="progress-row">
          <div class="progress-bar">
            <div class="progress-fill" [style.width.%]="getProgressPercentage(domainStatuses.get(domain)?.status || null)"></div>
          </div>
          <span class="progress-label">{{ getProgressPercentage(domainStatuses.get(domain)?.status || null) }}% · {{ formatProgress(domainStatuses.get(domain)?.status || null) }}</span>
        </div>

        <!-- Crawl stats row -->
        <div class="stats-row">
          <span class="stat-item"><span class="stat-lbl">Pages</span><span class="stat-val">{{ domainStatuses.get(domain)?.status?.totalUrls || 0 }}</span></span>
          <span class="stat-divider">·</span>
          <span class="stat-item"><span class="stat-lbl">Crawled</span><span class="stat-val ok">{{ domainStatuses.get(domain)?.status?.pagesCrawled || 0 }}</span></span>
          <span class="stat-divider">·</span>
          <span class="stat-item">
            <span class="stat-lbl">Failed</span>
            <span [class]="'stat-val' + ((domainStatuses.get(domain)?.status?.pagesFailed || 0) > 0 ? ' err' : '')">{{ domainStatuses.get(domain)?.status?.pagesFailed || 0 }}</span>
            <button *ngIf="(domainStatuses.get(domain)?.status?.pagesFailed || 0) > 0"
              class="failed-toggle" (click)="toggleFailedPages(domain)"
              [disabled]="failedPagesLoading.get(domain)">
              {{ failedPagesLoading.get(domain) ? '…' : (failedPagesExpanded.get(domain) ? '▲' : '▼') }}
            </button>
          </span>
          <span class="stat-divider">·</span>
          <span class="stat-item"><span class="stat-lbl">Cost</span><span class="stat-val">{{ domainStatuses.get(domain)?.status?.crawlCost | number:'1.4-4' }}</span></span>
        </div>

        <!-- Alias notice -->
        <div class="alias-enrichment-notice"
          *ngIf="processingStatuses.get(domain)?.enrichmentAliasDomains?.length">
          ⚡ Enrichment also queries
          <strong>{{ processingStatuses.get(domain)?.enrichmentAliasDomains?.join(', ') }}</strong>
          — data from the old domain maps to these pages by path.
        </div>

        <!-- Enrichment status table -->
        <table class="enrichment-table" *ngIf="processingStatuses.get(domain) as ps">
          <tbody>
            <tr>
              <td class="enr-lbl">Est. traffic</td>
              <td class="enr-count">
                <span [class]="'enr-badge enr-' + (ps.estTraffic?.status || 'not_started')">{{ ps.estTraffic?.status || 'not_started' }}</span>
                {{ ps.estTraffic?.pagesWithTraffic ?? 0 }} / {{ ps.estTraffic?.totalPages ?? 0 }} pages
              </td>
              <td class="enr-date">{{ ps.estTraffic?.lastFetchedAt ? (ps.estTraffic?.lastFetchedAt | date:'M/d/yy') : 'never' }}</td>
              <td class="enr-action">
                <button class="btn btn-outline btn-xs" (click)="refreshTraffic(domain)" [disabled]="trafficRefreshing.get(domain)">
                  {{ trafficRefreshing.get(domain) ? 'Running…' : 'Refresh' }}
                </button>
              </td>
            </tr>
            <tr>
              <td class="enr-lbl">Est. keywords</td>
              <td class="enr-count">
                <span [class]="'enr-badge enr-' + (ps.estKeywords?.status || 'not_started')">{{ ps.estKeywords?.status || 'not_started' }}</span>
                {{ ps.estKeywords?.pagesWithKeywords ?? 0 }} / {{ ps.estKeywords?.totalPages ?? 0 }} pages
              </td>
              <td class="enr-date">{{ ps.estKeywords?.lastFetchedAt ? (ps.estKeywords?.lastFetchedAt | date:'M/d/yy') : 'never' }}</td>
              <td class="enr-action">
                <button class="btn btn-outline btn-xs" (click)="refreshKeywords(domain)" [disabled]="keywordsRefreshing.get(domain)">
                  {{ keywordsRefreshing.get(domain) ? 'Running…' : 'Refresh' }}
                </button>
              </td>
            </tr>
            <tr>
              <td class="enr-lbl">Internal links</td>
              <td class="enr-count">
                <span [class]="'enr-badge enr-' + (ps.internalLinkSuggestions?.status || 'not_started')">{{ ps.internalLinkSuggestions?.status || 'not_started' }}</span>
                {{ ps.internalLinkSuggestions?.suggestionCount ?? 0 }} suggestions
              </td>
              <td class="enr-date">{{ ps.internalLinkSuggestions?.lastGeneratedAt ? (ps.internalLinkSuggestions?.lastGeneratedAt | date:'M/d/yy') : 'never' }}</td>
              <td class="enr-action">
                <button
                  class="btn btn-outline btn-xs"
                  (click)="refreshInternalLinks(domain)"
                  [disabled]="internalLinksRefreshing.get(domain) || !canRefreshInternalLinks(domain)"
                  [title]="internalLinksPrerequisiteHint(domain) || ''">
                  {{ internalLinksRefreshing.get(domain) ? 'Running…' : 'Build' }}
                </button>
              </td>
            </tr>
            <tr>
              <td class="enr-lbl">Supporting pages by topic</td>
              <td class="enr-count">
                <span [class]="'enr-badge enr-' + supportingPagesBuildBadge(ps.supportingPagesByTopic, isSupportingPagesByTopicRunning(domain, ps.supportingPagesByTopic))">{{
                  supportingPagesBuildBadge(ps.supportingPagesByTopic, isSupportingPagesByTopicRunning(domain, ps.supportingPagesByTopic))
                }}</span>
                {{ supportingPagesBuildProgressLabelRunning(ps.supportingPagesByTopic, isSupportingPagesByTopicRunning(domain, ps.supportingPagesByTopic)) }}
                <div
                  class="enr-progress-row"
                  *ngIf="isSupportingPagesByTopicRunning(domain, ps.supportingPagesByTopic)"
                >
                  <div class="enr-progress-bar">
                    <div
                      class="enr-progress-fill"
                      [style.width.%]="supportingPagesBuildProgressPercentRunning(ps.supportingPagesByTopic, isSupportingPagesByTopicRunning(domain, ps.supportingPagesByTopic))"
                    ></div>
                  </div>
                </div>
              </td>
              <td class="enr-date">{{ ps.supportingPagesByTopic?.lastGeneratedAt ? (ps.supportingPagesByTopic?.lastGeneratedAt | date:'M/d/yy') : 'never' }}</td>
              <td class="enr-action">
                <button
                  class="btn btn-outline btn-xs"
                  (click)="buildSupportingPagesByTopic(domain)"
                  [disabled]="!canBuildSupportingPagesByTopic(domain) || isSupportingPagesByTopicRunning(domain, ps.supportingPagesByTopic)"
                  [title]="supportingPagesByTopicPrerequisiteHint(domain) || ''">
                  {{ isSupportingPagesByTopicRunning(domain, ps.supportingPagesByTopic) ? 'Running…' : 'Build' }}
                </button>
              </td>
            </tr>
            <tr>
              <td class="enr-lbl">Supporting page opportunities</td>
              <td class="enr-count">
                <span [class]="'enr-badge enr-' + supportingPagesBuildBadge(ps.supportingPagesOpportunities, isSupportingPagesOpportunitiesRunning(domain, ps.supportingPagesOpportunities))">{{
                  supportingPagesBuildBadge(ps.supportingPagesOpportunities, isSupportingPagesOpportunitiesRunning(domain, ps.supportingPagesOpportunities))
                }}</span>
                {{ supportingPagesBuildProgressLabelRunning(ps.supportingPagesOpportunities, isSupportingPagesOpportunitiesRunning(domain, ps.supportingPagesOpportunities)) }}
                <div
                  class="enr-progress-row"
                  *ngIf="isSupportingPagesOpportunitiesRunning(domain, ps.supportingPagesOpportunities)"
                >
                  <div class="enr-progress-bar">
                    <div
                      class="enr-progress-fill"
                      [style.width.%]="supportingPagesBuildProgressPercentRunning(ps.supportingPagesOpportunities, isSupportingPagesOpportunitiesRunning(domain, ps.supportingPagesOpportunities))"
                    ></div>
                  </div>
                </div>
              </td>
              <td class="enr-date">{{ ps.supportingPagesOpportunities?.lastGeneratedAt ? (ps.supportingPagesOpportunities?.lastGeneratedAt | date:'M/d/yy') : 'never' }}</td>
              <td class="enr-action">
                <button
                  class="btn btn-outline btn-xs"
                  (click)="buildSupportingPagesOpportunities(domain)"
                  [disabled]="!canBuildSupportingPagesOpportunities(domain) || isSupportingPagesOpportunitiesRunning(domain, ps.supportingPagesOpportunities)"
                  [title]="supportingPagesOpportunitiesPrerequisiteHint(domain) || ''">
                  {{ isSupportingPagesOpportunitiesRunning(domain, ps.supportingPagesOpportunities) ? 'Running…' : 'Build' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <p class="enr-prereq-hint enr-prereq-hint--warn" *ngIf="processingStatusLoadHint(domain) as loadHint">
          {{ loadHint }}
        </p>
        <p class="enr-prereq-hint" *ngIf="internalLinksPrerequisiteHint(domain) as hint">
          Internal links: {{ hint }}
        </p>
        <p class="enr-prereq-hint" *ngIf="supportingPagesByTopicPrerequisiteHint(domain) as hint">
          Supporting pages by topic: {{ hint }}
        </p>
        <p class="enr-prereq-hint" *ngIf="supportingPagesOpportunitiesPrerequisiteHint(domain) as hint">
          Supporting page opportunities: {{ hint }}
        </p>

        <!-- Failed pages panel -->
        <div class="failed-pages-panel" *ngIf="failedPagesExpanded.get(domain) && failedPages.get(domain)">
          <div *ngIf="(failedPages.get(domain) || []).length === 0" class="failed-empty">No failed page details available.</div>
          <div class="failed-list" *ngIf="(failedPages.get(domain) || []).length > 0">
            <div class="failed-item" *ngFor="let page of failedPages.get(domain)">
              <div class="failed-url">{{ page.url }}</div>
              <div class="failed-err">{{ page.errorMessage }}</div>
              <div class="failed-meta" *ngIf="page.createdAt">{{ page.createdAt | date:'short' }}</div>
            </div>
          </div>
        </div>

      </div>

      <div *ngIf="!domainStatuses.get(domain)?.status && !domainStatuses.get(domain)?.loading" class="no-status">
        No crawl data available
      </div>
      <div *ngIf="domainStatuses.get(domain)?.loading && !domainStatuses.get(domain)?.status" class="loading-msg">
        Loading…
      </div>

    </div>
  </div>
</div>
